import { z } from 'zod';
import { contentStatusSchema } from './case';
import { contentUsesOnlyPublicMediaUrls } from '../case-content';
import { localizedTextSchema, optionalLocalizedTextSchema } from './localized';
import { fieldTranslationMetadataSchema, translationReviewSchema } from './translation-review';

const tiptapDocumentSchema = z
  .record(z.string(), z.unknown())
  .refine(contentUsesOnlyPublicMediaUrls, { message: 'Rich content images must use public media URLs.' });
const translationEntrySchema = z.object({
  block: tiptapDocumentSchema,
  sourceHash: z.string().min(1),
  origin: translationReviewSchema.shape.origin,
  reviewedHash: translationReviewSchema.shape.reviewedHash,
});
const contentSchema = z.object({
  master: tiptapDocumentSchema,
  sourceLanguage: z.enum(['nl', 'fr', 'en']),
  fieldMetadata: fieldTranslationMetadataSchema.optional(),
  translations: z
    .object({
      nl: z.record(z.string(), translationEntrySchema).optional(),
      fr: z.record(z.string(), translationEntrySchema).optional(),
      en: z.record(z.string(), translationEntrySchema).optional(),
    })
    .optional(),
});

export const blogArticleSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(160)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  title: localizedTextSchema,
  excerpt: optionalLocalizedTextSchema,
  content: contentSchema.optional(),
  status: contentStatusSchema.default('draft'),
  isFeatured: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  seoDescription: optionalLocalizedTextSchema,
  reviewNotes: z.string().max(4000).nullable().optional(),
  media: z
    .array(
      z.object({
        mediaAssetId: z.string().uuid(),
        role: z.enum(['hero', 'gallery', 'content']).default('hero'),
        sortOrder: z.number().int().min(0).default(0),
        cropX: z.number().int().min(0).max(100).default(50),
      })
    )
    .default([]),
});

export const createBlogArticleSchema = blogArticleSchema.omit({ id: true });
export const updateBlogArticleSchema = blogArticleSchema.partial().omit({ id: true });
export type BlogArticleFormData = z.infer<typeof createBlogArticleSchema>;
